import React, { useState, useEffect, useCallback } from 'react';
import baseapi from '../../../services/baseapi';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight, 
  Search, 
  RefreshCw,
  Network,
  Database,
  Tag,
  Layers,
  Cpu,
  Globe,
  Activity,
  HardDrive,
  FolderOpen
} from 'lucide-react';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';

interface ServicesTabProps {
  agentId: string;
  clientId: string;
}

const ServicesTab: React.FC<ServicesTabProps> = ({ agentId, clientId }) => {
  const [loadingServices, setLoadingServices] = useState(true);
  const [services, setServices] = useState<any[]>([]);
  const [servicesPage, setServicesPage] = useState<number>(1);
  const [servicesItemsPerPage, setServicesItemsPerPage] = useState<number>(30);
  const [servicesTotal, setServicesTotal] = useState<number>(0);
  const [servicesSearch, setServicesSearch] = useState('');
  const [servicesDebouncedSearch, setServicesDebouncedSearch] = useState('');

  const [loadingPvcs, setLoadingPvcs] = useState(true);
  const [pvcs, setPvcs] = useState<any[]>([]);
  const [pvcsPage, setPvcsPage] = useState<number>(1);
  const [pvcsItemsPerPage, setPvcsItemsPerPage] = useState<number>(30);
  const [pvcsTotal, setPvcsTotal] = useState<number>(0);
  const [pvcsSearch, setPvcsSearch] = useState('');
  const [pvcsDebouncedSearch, setPvcsDebouncedSearch] = useState('');

  // Debounce services search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setServicesDebouncedSearch(servicesSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [servicesSearch]);

  // Debounce PVCs search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setPvcsDebouncedSearch(pvcsSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [pvcsSearch]);

  // Reset page when search changes
  useEffect(() => {
    setServicesPage(1);
  }, [servicesDebouncedSearch]);

  useEffect(() => {
    setPvcsPage(1);
  }, [pvcsDebouncedSearch]);

  // Reset pages and searches when cluster agent changes
  useEffect(() => {
    setServicesPage(1);
    setPvcsPage(1);
    setServicesSearch('');
    setServicesDebouncedSearch('');
    setPvcsSearch('');
    setPvcsDebouncedSearch('');
  }, [agentId, clientId]);

  // Fetch Services
  const fetchServices = useCallback(async () => {
    setLoadingServices(true);
    try {
      const skip = (servicesPage - 1) * servicesItemsPerPage;
      const queryObj: any = {};

      if (servicesDebouncedSearch) {
        queryObj.$or = [
          { service_name: { "$ilike": `%${servicesDebouncedSearch}%` } },
          { name: { "$ilike": `%${servicesDebouncedSearch}%` } },
          { namespace: { "$ilike": `%${servicesDebouncedSearch}%` } },
          { type: { "$ilike": `%${servicesDebouncedSearch}%` } },
          { cluster_ip: { "$ilike": `%${servicesDebouncedSearch}%` } },
          { external_ip: { "$ilike": `%${servicesDebouncedSearch}%` } }
        ];
      }

      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = {
        agent_id: agentId,
        client_id: clientId,
        skip,
        limit: servicesItemsPerPage
      };
      if (clusterName) {
        params.cluster_name = clusterName;
      }

      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }

      const res = await baseapi.get('/k8s_dashboard/services', { params });
      if (res.data?.status) {
        const fetchedServices = res.data.data?.services || res.data.data || [];
        setServices(fetchedServices);
        setServicesTotal(res.data.count || res.data.total || res.data.data?.total || fetchedServices.length || 0);
      }
    } catch (error) {
      console.error('Error fetching services data:', error);
    } finally {
      setLoadingServices(false);
    }
  }, [agentId, clientId, servicesPage, servicesItemsPerPage, servicesDebouncedSearch]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Fetch PVCs
  const fetchPvcs = useCallback(async () => {
    setLoadingPvcs(true);
    try {
      const skip = (pvcsPage - 1) * pvcsItemsPerPage;
      const queryObj: any = {};

      if (pvcsDebouncedSearch) {
        queryObj.$or = [
          { pvc_name: { "$ilike": `%${pvcsDebouncedSearch}%` } },
          { name: { "$ilike": `%${pvcsDebouncedSearch}%` } },
          { namespace: { "$ilike": `%${pvcsDebouncedSearch}%` } },
          { status: { "$ilike": `%${pvcsDebouncedSearch}%` } },
          { phase: { "$ilike": `%${pvcsDebouncedSearch}%` } },
          { storage_class: { "$ilike": `%${pvcsDebouncedSearch}%` } },
          { volume_name: { "$ilike": `%${pvcsDebouncedSearch}%` } },
          { volume: { "$ilike": `%${pvcsDebouncedSearch}%` } }
        ];
      }

      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = {
        agent_id: agentId,
        client_id: clientId,
        skip,
        limit: pvcsItemsPerPage
      };
      if (clusterName) {
        params.cluster_name = clusterName;
      }

      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }

      const res = await baseapi.get('/k8s_dashboard/pvcs', { params });
      if (res.data?.status) {
        const fetchedPvcs = res.data.data || [];
        setPvcs(fetchedPvcs);
        setPvcsTotal(res.data.count || res.data.total || fetchedPvcs.length || 0);
      }
    } catch (error) {
      console.error('Error fetching PVCs data:', error);
    } finally {
      setLoadingPvcs(false);
    }
  }, [agentId, clientId, pvcsPage, pvcsItemsPerPage, pvcsDebouncedSearch]);

  useEffect(() => {
    fetchPvcs();
  }, [fetchPvcs]);

  const servicesTotalPages = Math.max(1, Math.ceil(servicesTotal / servicesItemsPerPage));
  const servicesStartIndex = (servicesPage - 1) * servicesItemsPerPage;
  const servicesEndIndex = servicesStartIndex + services.length;

  const handleServicesPageChange = (page: number): void => {
    if (page >= 1 && page <= servicesTotalPages) {
      setServicesPage(page);
    }
  };

  const handleServicesItemsPerPageChange = (value: number): void => {
    setServicesItemsPerPage(value);
    setServicesPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Services Section */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden flex flex-col">
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between p-3 border-b border-gray-700 bg-gray-900/30">
          <h3 className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-400 m-0 font-mono">
            <Network className="w-4 h-4 text-orange-500" />
            Services
          </h3>
          <div className="flex gap-2 w-full sm:w-auto items-center">
            <div className="text-gray-500 font-mono text-[10px] bg-gray-900/50 px-2 py-1 rounded border border-gray-800">
              {servicesTotal > 0 ? `${servicesStartIndex + 1} - ${servicesEndIndex} of ${servicesTotal}` : '0 services'}
            </div>
            <button
              onClick={fetchServices}
              className="w-8 h-8 flex items-center justify-center rounded bg-gray-800 border border-gray-700 text-gray-400 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all"
              title="Refresh services"
            >
              <RefreshCw size={14} className={loadingServices ? "animate-spin" : ""} />
            </button>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder="search services..."
                value={servicesSearch}
                onChange={(e) => setServicesSearch(e.target.value)}
                className="bg-gray-900/50 border border-gray-700/50 text-gray-200 text-xs rounded pl-8 pr-3 py-1.5 w-full sm:w-48 focus:outline-none focus:border-orange-500/50 transition-colors placeholder:text-gray-600 font-mono h-8"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800">
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-orange-400" />
                    Name
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-orange-400" />
                    Namespace
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Cpu className="w-3.5 h-3.5 text-orange-400" />
                    Type
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Network className="w-3.5 h-3.5 text-orange-400" />
                    Cluster IP
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-orange-400" />
                    External IP
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingServices ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                      <p className="text-gray-400 font-medium">Loading services...</p>
                    </div>
                  </td>
                </tr>
              ) : services.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-500 italic">No services found</td>
                </tr>
              ) : (
                services.map((svc, idx) => (
                  <tr key={idx} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                    <td className="p-2 text-gray-200">{svc.service_name || svc.name}</td>
                    <td className="p-2 text-gray-400">{svc.namespace}</td>
                    <td className="p-2 text-gray-300">{svc.type}</td>
                    <td className="p-2 text-gray-400">{svc.cluster_ip}</td>
                    <td className="p-2 text-gray-400">{svc.external_ip}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer / Pagination */}
        <div className="bg-gray-800/80 border-t border-gray-700 px-3 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <ItemsPerPageDropdown
              value={servicesItemsPerPage}
              onChange={(val: any) => handleServicesItemsPerPageChange(val)}
            />
            {servicesTotal > 0 ? (
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest font-mono">
                <span>{servicesStartIndex + 1}</span> - <span>{servicesEndIndex}</span> OF {servicesTotal}
              </span>
            ) : (
              services.length > 0 && (
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest font-mono">
                  <span>{servicesStartIndex + 1}</span> - <span>{servicesStartIndex + services.length}</span>
                </span>
              )
            )}
          </div>
          <div className="flex items-center gap-1 font-mono">
            <button
              onClick={() => handleServicesPageChange(1)}
              disabled={servicesPage === 1}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="First Page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleServicesPageChange(servicesPage - 1)}
              disabled={servicesPage === 1}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-2 py-0.5 bg-orange-600 rounded text-white text-[10px] font-bold mx-1 shadow-md shadow-orange-950/20">
              {servicesPage}
            </div>
            <button
              onClick={() => handleServicesPageChange(servicesPage + 1)}
              disabled={services.length < servicesItemsPerPage || (servicesTotal > 0 && servicesPage === servicesTotalPages)}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {servicesTotal > 0 && (
              <button
                onClick={() => handleServicesPageChange(servicesTotalPages)}
                disabled={servicesPage === servicesTotalPages}
                className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Persistent Volume Claims (PVC) Section */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden flex flex-col">
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between p-3 border-b border-gray-700 bg-gray-900/30">
          <h3 className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-400 m-0 font-mono">
            <Database className="w-4 h-4 text-orange-500" />
            Persistent Volume Claims
          </h3>
          <div className="flex gap-2 w-full sm:w-auto items-center">
            <div className="text-gray-500 font-mono text-[10px] bg-gray-900/50 px-2 py-1 rounded border border-gray-800">
              {pvcsTotal > 0 ? `${(pvcsPage - 1) * pvcsItemsPerPage + 1} - ${Math.min(pvcsPage * pvcsItemsPerPage, pvcsTotal)} of ${pvcsTotal}` : '0 PVCs'}
            </div>
            <button
              onClick={fetchPvcs}
              className="w-8 h-8 flex items-center justify-center rounded bg-gray-800 border border-gray-700 text-gray-400 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all"
              title="Refresh PVCs"
            >
              <RefreshCw size={14} className={loadingPvcs ? "animate-spin" : ""} />
            </button>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder="search PVCs..."
                value={pvcsSearch}
                onChange={(e) => setPvcsSearch(e.target.value)}
                className="bg-gray-900/50 border border-gray-700/50 text-gray-200 text-xs rounded pl-8 pr-3 py-1.5 w-full sm:w-48 focus:outline-none focus:border-orange-500/50 transition-colors placeholder:text-gray-600 font-mono h-8"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800">
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-orange-400" />
                    Name
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-orange-400" />
                    Namespace
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-orange-400" />
                    Status
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <HardDrive className="w-3.5 h-3.5 text-orange-400" />
                    Capacity
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <Database className="w-3.5 h-3.5 text-orange-400" />
                    Storage Class
                  </div>
                </th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">
                  <div className="flex items-center gap-1.5">
                    <FolderOpen className="w-3.5 h-3.5 text-orange-400" />
                    Volume
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingPvcs ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                      <p className="text-gray-400 font-medium">Loading PVCs...</p>
                    </div>
                  </td>
                </tr>
              ) : pvcs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-gray-500 italic">No PVCs found</td>
                </tr>
              ) : (
                pvcs.map((pvc, idx) => {
                  const phase = pvc.phase || pvc.status || 'Unknown';
                  const isBound = phase === 'Bound';
                  const isPending = phase === 'Pending';
                  const isLost = phase === 'Lost' || phase === 'Failed';

                  return (
                    <tr key={idx} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                      <td className="p-2 text-gray-200">{pvc.pvc_name || pvc.name}</td>
                      <td className="p-2 text-gray-400">{pvc.namespace}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                          isBound
                            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                            : isPending
                              ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
                              : isLost
                                ? 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
                                : 'bg-gray-700 text-gray-300'
                        }`}>
                          {phase}
                        </span>
                      </td>
                      <td className="p-2 text-gray-400">{pvc.capacity || '—'}</td>
                      <td className="p-2 text-gray-400">{pvc.storage_class || '—'}</td>
                      <td className="p-2 text-gray-400">{pvc.volume_name || pvc.volume || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer / Pagination for PVCs */}
        <div className="bg-gray-800/80 border-t border-gray-700 px-3 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <ItemsPerPageDropdown
              value={pvcsItemsPerPage}
              onChange={(val: any) => {
                setPvcsItemsPerPage(val);
                setPvcsPage(1);
              }}
            />
            {pvcsTotal > 0 ? (
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest font-mono">
                <span>{(pvcsPage - 1) * pvcsItemsPerPage + 1}</span> - <span>{Math.min(pvcsPage * pvcsItemsPerPage, pvcsTotal)}</span> OF {pvcsTotal}
              </span>
            ) : (
              pvcs.length > 0 && (
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest font-mono">
                  <span>{(pvcsPage - 1) * pvcsItemsPerPage + 1}</span> - <span>{(pvcsPage - 1) * pvcsItemsPerPage + pvcs.length}</span>
                </span>
              )
            )}
          </div>
          <div className="flex items-center gap-1 font-mono">
            <button
              onClick={() => setPvcsPage(1)}
              disabled={pvcsPage === 1}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="First Page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPvcsPage(pvcsPage - 1)}
              disabled={pvcsPage === 1}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-2 py-0.5 bg-orange-600 rounded text-white text-[10px] font-bold mx-1 shadow-md shadow-orange-950/20">
              {pvcsPage}
            </div>
            <button
              onClick={() => setPvcsPage(pvcsPage + 1)}
              disabled={pvcs.length < pvcsItemsPerPage || (pvcsTotal > 0 && pvcsPage === Math.max(1, Math.ceil(pvcsTotal / pvcsItemsPerPage)))}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {pvcsTotal > 0 && (
              <button
                onClick={() => setPvcsPage(Math.max(1, Math.ceil(pvcsTotal / pvcsItemsPerPage)))}
                disabled={pvcsPage === Math.max(1, Math.ceil(pvcsTotal / pvcsItemsPerPage))}
                className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesTab;
